import { Header } from '@tanstack/react-table';
import { RefObject, useEffect, useMemo, useState } from 'react';
import { debounce, DebounceTimeoutValues, Size } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { TextInput } from '../../../../TextInput';
import { ColumnFilter, ColumnFilterType, Filter, NumberFilterType, StringFilterType } from '../../../types';
import { FilterContainer, FilterLabel } from '../styles';

interface TableColumnTextFilterProps<T> {
    initialValue: string | number;
    header: Header<T, unknown>;
    setColumnFilter: (value?: ColumnFilter) => void;
    textInputRef: RefObject<HTMLElement>;
}

const TableColumnInputFilter = <T,>({
    initialValue,
    header,
    setColumnFilter,
    textInputRef,
}: TableColumnTextFilterProps<T>) => {
    /*
     * We need a local state here to enable input debouncing without UI issues.
     * If we use values from main table state directly and onStateChange is debounced,
     * updates from user interactions are delayed.
     */
    const [textInputValue, setTextInputValue] = useState(initialValue);

    const debouncedSetColumnFilter = useMemo(
        () => debounce(setColumnFilter, DebounceTimeoutValues.textInput),
        [setColumnFilter]
    );

    useEffect(() => {
        setTextInputValue(initialValue);
    }, [initialValue]);

    const { t } = useLocalization();

    const handleTextInputChange = (payload?: Filter<StringFilterType, string> | Filter<NumberFilterType, number>) => {
        setTextInputValue(payload?.value ?? '');
        debouncedSetColumnFilter(payload);
    };

    const commonProps = {
        name: header.column.id,
        id: header.column.id,
        size: 'small' as Size,
        indicativeIcon: IconName.Search,
        clearable: true,
        value: textInputValue.toString(),
        ref: textInputRef,
        inline: true,
    };

    let component = null;

    switch (header.column.columnDef.meta?.filtersType) {
        case ColumnFilterType.String:
            /**
             * There is currently disconnection between columns definition and potential filters on them; and
             * the actual filter values provided on state, so we need to manually cast to it.
             * Potential improvement would be to derive TableState<T> from column definition and conditionally
             * look for defined filters and their types.
             */
            component = (
                <TextInput
                    {...commonProps}
                    placeholder={t(OptimusTextKey.TableFilterContains)}
                    onChange={(e, value) =>
                        handleTextInputChange(
                            value === ''
                                ? undefined
                                : {
                                      filterType: StringFilterType.Contains,
                                      value: String(value),
                                  }
                        )
                    }
                />
            );

            break;

        case ColumnFilterType.Number:
            component = (
                <TextInput
                    {...commonProps}
                    type="number"
                    placeholder={t(OptimusTextKey.TableFilterGreaterThan)}
                    onChange={(e, value) =>
                        handleTextInputChange(
                            value === ''
                                ? undefined
                                : {
                                      filterType: NumberFilterType.GreaterThan,
                                      value: Number(value),
                                  }
                        )
                    }
                />
            );

            break;

        default:
            component = null;
    }

    if (!component) {
        return null;
    }

    return (
        <FilterContainer vertical spacing="100">
            <FilterLabel htmlFor={commonProps.name}>{t(OptimusTextKey.TableFilterBySearch)}</FilterLabel>
            {component}
        </FilterContainer>
    );
};

export default TableColumnInputFilter;
